import { motion } from "framer-motion";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GoogleRecaptcha from "@/components/GoogleRecaptcha";
import { insertContactSchema } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { usePublicConfig } from "@/lib/publicConfig";
import { trackEvent } from "@/lib/analytics";
import { z } from "zod";

export default function Contact() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaResetSignal, setCaptchaResetSignal] = useState(0);
  const { data: publicConfig, isLoading: isPublicConfigLoading } = usePublicConfig();
  const recaptcha = publicConfig?.recaptcha ?? { enabled: false, siteKey: "" };

  const form = useForm<z.infer<typeof insertContactSchema>>({
    resolver: zodResolver(insertContactSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      service: "",
      budget: "",
      message: "",
    },
  });

  const contactMutation = useMutation({
    mutationFn: async (data: z.infer<typeof insertContactSchema> & { captchaToken?: string }) => {
      const res = await apiRequest("POST", "/api/contacts", data);
      return res.json();
    },
    onSuccess: () => {
      trackEvent("generate_lead", {
        form: "contact",
        service: form.getValues("service") || "unspecified",
      });
      toast({
        title: "Message Sent",
        description: "We’ll get back to you shortly.",
      });
      form.reset();
      setCaptchaResetSignal((value) => value + 1);
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
    },
    onError: () => {
      setCaptchaResetSignal((value) => value + 1);
      toast({
        title: "Error",
        description: "Something went wrong. Try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: z.infer<typeof insertContactSchema>) => {
    if (recaptcha.enabled && !captchaToken) {
      toast({
        title: "Security Check Pending",
        description: "Please wait for the security verification before sending your message.",
        variant: "destructive",
      });
      return;
    }

    contactMutation.mutate({
      ...data,
      captchaToken: recaptcha.enabled ? captchaToken : undefined,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
      <Navigation />

      {/* Section */}
      <section className="relative overflow-hidden py-14 sm:py-20 px-4 sm:px-6 lg:px-8">
        {/* Background decorative elements (hidden on mobile) */}
        <div className="hidden sm:block absolute inset-0">
          <div className="absolute top-0 h-96 w-full bg-gradient-to-br from-blue-50/40 via-transparent to-cyan-50/30 dark:from-blue-900/20 dark:to-cyan-900/20" />
          <div className="absolute bottom-0 h-96 w-full bg-gradient-to-t from-purple-50/20 to-transparent dark:from-purple-900/10" />
        </div>

        <div className="container mx-auto relative z-10">
          {/* Header */}
          <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                Get in Touch
              </span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300">
              Share your requirements and we’ll respond with the right plan.
            </p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {/* LEFT - Contact Information */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="rounded-2xl sm:rounded-3xl bg-white border border-gray-200 p-5 sm:p-8 shadow-xl dark:bg-gray-800 dark:border-gray-700">
                <h3 className="text-xl sm:text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                  Contact Information
                </h3>

                <div className="space-y-4 mb-6">
                  {[
                    {
                      icon: "fas fa-map-marker-alt",
                      title: "Office Address",
                      content:
                        "F-26, Gautam Marg, Vaishali Nagar, Jaipur",
                      href: undefined as string | undefined,
                    },
                    {
                      icon: "fas fa-phone",
                      title: "Phone",
                      content: "+91 73000 75187",
                      href: "tel:+917300075187",
                    },
                    {
                      icon: "fas fa-users-cog",
                      title: "Micro-GCC Discussions",
                      content: "+91 89520 12870",
                      href: "tel:+918952012870",
                    },
                    {
                      icon: "fas fa-envelope",
                      title: "Email",
                      content: "info@variablesoft.com",
                      href: "mailto:info@variablesoft.com",
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700"
                    >
                      <div className="h-10 w-10 rounded-lg bg-blue-600 text-white grid place-items-center">
                        <i className={item.icon} />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {item.title}
                        </p>
                        {item.href ? (
                          <a
                            href={item.href}
                            className="text-sm text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition-colors"
                          >
                            {item.content}
                          </a>
                        ) : (
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {item.content}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Image */}
                <img
                  src="https://img.freepik.com/premium-photo/black-home-phone-black-background-3d-illustration_291814-844.jpg"
                  className="w-full h-44 sm:h-64 object-cover rounded-xl"
                  loading="lazy"
                  alt="Contact"
                />

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
                  {[
                    ["4.9/5", "Client Rating"],
                    ["24/7", "Support"],
                    ["500+", "Projects Delivered"],
                  ].map(([t, s], i) => (
                    <div
                      key={i}
                      className="text-center p-3 rounded-xl border border-gray-200 dark:border-gray-700"
                    >
                      <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                        {t}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{s}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* RIGHT - Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="rounded-2xl sm:rounded-3xl bg-white border border-gray-200 p-5 sm:p-8 shadow-xl dark:bg-gray-800 dark:border-gray-700">
                <h3 className="text-xl sm:text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                  Send Us a Message
                </h3>

                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      placeholder="First Name"
                      {...form.register("firstName")}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                    />
                    <Input
                      placeholder="Last Name"
                      {...form.register("lastName")}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                    />
                  </div>

                  <Input
                    placeholder="Email"
                    {...form.register("email")}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                  />
                  <Input
                    placeholder="Phone"
                    {...form.register("phone")}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                  />

                  <Select
                    value={form.watch("service")}
                    onValueChange={(v) =>
                      form.setValue("service", v, { shouldValidate: true })
                    }
                  >
                    <SelectTrigger className="h-11 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                      <SelectValue placeholder="Service Needed" />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-gray-800 dark:border-gray-700">
                      <SelectItem value="web" className="dark:text-white dark:focus:bg-gray-700">Web Development</SelectItem>
                      <SelectItem value="app" className="dark:text-white dark:focus:bg-gray-700">Mobile App</SelectItem>
                      <SelectItem value="seo" className="dark:text-white dark:focus:bg-gray-700">SEO</SelectItem>
                    </SelectContent>
                  </Select>

                  <Textarea
                    rows={4}
                    placeholder="Tell us about your project"
                    {...form.register("message")}
                    className="resize-none dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                  />

                  <GoogleRecaptcha
                    enabled={recaptcha.enabled}
                    siteKey={recaptcha.siteKey}
                    resetSignal={captchaResetSignal}
                    onChange={setCaptchaToken}
                  />

                  <Button
                    type="submit"
                    className="w-full py-4 text-base sm:text-lg"
                    disabled={
                      contactMutation.isPending ||
                      isPublicConfigLoading ||
                      (recaptcha.enabled && !captchaToken)
                    }
                  >
                    {contactMutation.isPending ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
